import type { Atlas, MenuAction, Mode, Rect } from './types';
import { pose } from './atlas';

/**
 * Owns every DOM node Buntu draws into, and nothing else.
 *
 * The renderer is deliberately dumb: `apply()` writes a frame of state onto
 * the nodes and makes no decisions. All behaviour lives in the engine, which
 * keeps this file portable to a desktop shell.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const STYLE_ID = 'buntu-companion-style';

/** Display height of the floating head, in CSS pixels (~80% of original 132). */
export const HEAD_H = 106;

/** One frame of everything the renderer needs to know. */
export interface FrameState {
  mode: Mode;
  /** Character centre, in viewport pixels. */
  x: number;
  y: number;
  /** -1..1, where the eyes are pointed. */
  gazeX: number;
  gazeY: number;
  /** 0 open, 1 shut. */
  blink: number;
  /** -1..1, subtle head tilt that leads the gaze. */
  yaw: number;
  /** Extra vertical offset for the idle float and walk bob, in pixels. */
  bob: number;
  /** Squash factor for the walk cycle; 1 is neutral. */
  squash: number;
  poseName: string;
  /** 1 faces right, -1 faces left. */
  facing: 1 | -1;
  opacity: number;
  bubble: string | null;
  /** Admin broadcasts get a dismiss control; wellness lines fade on their own. */
  bubbleDismissible?: boolean;
  /** True while the user is holding and dragging Buntu. */
  dragging: boolean;
  /** Character tucked away; restore chip is shown instead. */
  minimized: boolean;
}

function css(): string {
  return `
.buntu-root{position:fixed;inset:0;pointer-events:none;z-index:9000;
  contain:layout style;}
.buntu-stage{position:absolute;left:0;top:0;pointer-events:auto;cursor:grab;
  will-change:transform,opacity;transform-origin:50% 100%;
  -webkit-user-select:none;user-select:none;-webkit-user-drag:none;touch-action:none;}
.buntu-stage[data-dragging="1"]{cursor:grabbing;}
.buntu-layer{position:absolute;left:0;top:0;transform-origin:50% 100%;}
.buntu-layer[hidden]{display:none;}
.buntu-img{display:block;width:100%;height:100%;
  filter:drop-shadow(0 6px 10px rgba(15,23,42,.28));
  -webkit-user-drag:none;pointer-events:none;}
.buntu-face{position:absolute;overflow:visible;pointer-events:none;}
.buntu-eye,.buntu-mouth{transition:none;}
.buntu-bubble{position:absolute;left:0;top:0;max-width:190px;
  padding:8px 11px;border-radius:11px;
  background:#fff;color:#253546;
  font:500 11.5px/1.45 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;
  box-shadow:0 8px 26px rgba(15,23,42,.20),0 0 0 1px rgba(37,53,70,.09);
  opacity:0;transform:translate(-50%,-100%) scale(.9);transform-origin:50% 120%;
  transition:opacity .26s ease,transform .26s cubic-bezier(.34,1.56,.64,1);
  pointer-events:none;white-space:normal;}
.buntu-bubble[data-show="1"]{opacity:1;transform:translate(-50%,-100%) scale(1);}
.buntu-bubble[data-dismissible="1"]{padding-right:26px;}
.buntu-bubble-close{position:absolute;top:4px;right:4px;width:18px;height:18px;
  display:none;align-items:center;justify-content:center;padding:0;
  appearance:none;border:0;border-radius:50%;background:#eef2f7;color:#64748b;
  cursor:pointer;font:700 12px/1 ui-sans-serif,system-ui,sans-serif;
  /* The bubble itself stays click-through so it never blocks the desktop. */
  pointer-events:auto;}
.buntu-bubble[data-dismissible="1"] .buntu-bubble-close{display:flex;}
.buntu-bubble-close:hover{background:#dbe3ec;color:#1e293b;}
.buntu-bubble::after{content:"";position:absolute;left:var(--buntu-tail,50%);
  bottom:-6px;width:12px;height:12px;background:#fff;
  transform:translateX(-50%) rotate(45deg);
  box-shadow:2px 2px 0 rgba(37,53,70,.05);border-radius:2px;}
.buntu-menu{position:absolute;left:0;top:0;display:flex;flex-direction:column;
  gap:2px;padding:5px;border-radius:11px;background:#fff;min-width:158px;
  box-shadow:0 10px 30px rgba(15,23,42,.22),0 0 0 1px rgba(37,53,70,.09);
  opacity:0;visibility:hidden;transform:translate(-50%,-100%) scale(.94);
  transform-origin:50% 110%;pointer-events:none;
  transition:opacity .16s ease,transform .16s ease,visibility .16s;}
.buntu-menu[data-open="1"]{opacity:1;visibility:visible;pointer-events:auto;
  transform:translate(-50%,-100%) scale(1);}
.buntu-menu button{appearance:none;border:0;background:transparent;
  text-align:left;padding:7px 10px;border-radius:7px;cursor:pointer;color:#253546;
  font:500 12.5px/1.2 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;}
.buntu-menu button:hover,.buntu-menu button:focus-visible{background:#eef2f7;
  outline:none;}
.buntu-inbox{position:absolute;left:0;top:0;width:min(280px,72vw);max-height:min(320px,50vh);
  display:flex;flex-direction:column;background:#fff;border-radius:12px;overflow:hidden;
  box-shadow:0 14px 36px rgba(15,23,42,.28),0 0 0 1px rgba(37,53,70,.1);
  opacity:0;visibility:hidden;transform:translate(-50%,-100%) scale(.94);
  transform-origin:50% 110%;pointer-events:none;z-index:2;
  transition:opacity .16s ease,transform .16s ease,visibility .16s;}
.buntu-inbox[data-open="1"]{opacity:1;visibility:visible;pointer-events:auto;
  transform:translate(-50%,-100%) scale(1);}
.buntu-inbox-head{display:flex;align-items:center;justify-content:space-between;
  gap:8px;padding:10px 12px;border-bottom:1px solid #e8eef5;background:#f7fafc;}
.buntu-inbox-head strong{font:650 12.5px/1.2 ui-sans-serif,system-ui,sans-serif;color:#1e293b;}
.buntu-inbox-head button{appearance:none;border:0;background:#e2e8f0;color:#334155;
  border-radius:7px;padding:4px 8px;cursor:pointer;font:600 11px/1 ui-sans-serif,system-ui,sans-serif;}
.buntu-inbox-list{overflow:auto;padding:6px;display:flex;flex-direction:column;gap:6px;}
.buntu-inbox-empty{padding:18px 12px;text-align:center;color:#64748b;
  font:500 12px/1.4 ui-sans-serif,system-ui,sans-serif;}
.buntu-inbox-item{border:1px solid #e8eef5;border-radius:9px;padding:8px 9px;background:#fff;}
.buntu-inbox-meta{display:flex;justify-content:space-between;gap:8px;margin-bottom:4px;
  font:600 10.5px/1.2 ui-sans-serif,system-ui,sans-serif;color:#64748b;}
.buntu-inbox-from{color:#0f766e;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:55%;}
.buntu-inbox-body{font:500 12px/1.4 ui-sans-serif,system-ui,sans-serif;color:#1e293b;
  white-space:pre-wrap;word-break:break-word;}
.buntu-inbox-more{appearance:none;border:1px dashed #cbd5e1;background:#f8fafc;
  color:#475569;border-radius:9px;padding:7px 9px;cursor:pointer;
  font:600 11.5px/1.2 ui-sans-serif,system-ui,sans-serif;}
.buntu-inbox-more:hover{background:#eef2f7;color:#1e293b;}
.buntu-restore{position:fixed;right:14px;bottom:56px;z-index:9001;
  display:none;align-items:center;gap:8px;padding:7px 12px 7px 7px;
  border:0;border-radius:999px;cursor:pointer;pointer-events:auto;
  background:#fff;color:#253546;
  font:600 12.5px/1 ui-sans-serif,system-ui,-apple-system,"Segoe UI",sans-serif;
  box-shadow:0 8px 22px rgba(15,23,42,.22),0 0 0 1px rgba(37,53,70,.08);
  transition:transform .15s ease,box-shadow .15s ease;}
.buntu-restore[data-show="1"]{display:inline-flex;}
.buntu-restore:hover,.buntu-restore:focus-visible{transform:translateY(-1px);
  box-shadow:0 10px 26px rgba(15,23,42,.28),0 0 0 1px rgba(37,53,70,.1);
  outline:none;}
.buntu-restore img{width:28px;height:28px;border-radius:50%;object-fit:cover;
  object-position:center 20%;background:#eef2f7;pointer-events:none;}
@media (prefers-reduced-motion:reduce){
  .buntu-bubble,.buntu-menu,.buntu-inbox,.buntu-restore{transition-duration:.01ms;}
}`;
}

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = css();
  document.head.appendChild(el);
}

function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

export class Renderer {
  private readonly atlas: Atlas;
  private readonly assetUrl: (src: string) => string;

  readonly root: HTMLElement;
  readonly stage: HTMLElement;
  /** Display size of each layer, so the engine can position by anchor. */
  readonly headSize: { w: number; h: number };
  readonly walkSize: { w: number; h: number };

  private readonly headLayer: HTMLElement;
  private readonly walkLayer: HTMLElement;
  private readonly poseImg: HTMLImageElement;
  private readonly face: SVGSVGElement;
  private readonly eyes: SVGGElement[] = [];
  private mouth!: SVGPathElement;
  private readonly bubble: HTMLElement;
  private readonly bubbleText: HTMLElement;
  private readonly bubbleClose: HTMLButtonElement;
  /** Fired when the reader closes a broadcast bubble. */
  onDismiss?: () => void;
  private readonly menu: HTMLElement;
  private readonly inbox: HTMLElement;
  private readonly inboxList: HTMLElement;
  private readonly restore: HTMLButtonElement;
  private menuTimer = 0;
  private menuLocked = false;
  private inboxOpen = false;
  private lastAnchor = { x: 0, y: 0, above: 0 };
  private onDocKey?: (e: KeyboardEvent) => void;
  private onDocClick?: (e: MouseEvent) => void;

  /** Invoked when the user picks something from the hover menu. */
  onAction?: (action: MenuAction) => void;
  /** Invoked when the user clicks the minimized restore chip. */
  onRestore?: () => void;

  /** Geometry of the head's face panel, in head-display pixels. */
  private readonly panel: Rect;
  private readonly eyeBoxes: Rect[] = [];
  private currentPose = '';
  private currentBubble: string | null = null;

  constructor(mount: HTMLElement, atlas: Atlas, assetUrl: (src: string) => string) {
    ensureStyle();
    this.atlas = atlas;
    this.assetUrl = assetUrl;

    const head = atlas.head;
    const headScale = HEAD_H / head.h;
    const headW = head.w * headScale;
    this.headSize = { w: headW, h: HEAD_H };

    // Scale the full body so its face screen matches the bust's. Sizing both
    // layers by overall height instead would make the walking Buntu look like
    // a smaller creature, since the bust is cropped to head and shoulders
    // while a pose frame holds the whole character.
    const refScreenW = (pose(atlas, 'idle')?.screen.w ?? atlas.frame.w * 0.5);
    const walkScale = (head.screen.w * headScale) / refScreenW;
    this.walkSize = {
      w: atlas.frame.w * walkScale,
      h: atlas.frame.h * walkScale,
    };
    this.panel = {
      x: head.screen.x * headScale,
      y: head.screen.y * headScale,
      w: head.screen.w * headScale,
      h: head.screen.h * headScale,
    };

    this.root = document.createElement('div');
    this.root.className = 'buntu-root';

    this.stage = document.createElement('div');
    this.stage.className = 'buntu-stage';
    this.stage.setAttribute('role', 'button');
    this.stage.setAttribute('tabindex', '0');
    this.stage.setAttribute('aria-label', 'Buntu assistant');

    // --- floating head -----------------------------------------------------
    this.headLayer = document.createElement('div');
    this.headLayer.className = 'buntu-layer';
    this.headLayer.style.width = `${headW}px`;
    this.headLayer.style.height = `${HEAD_H}px`;

    const headImg = document.createElement('img');
    headImg.className = 'buntu-img';
    headImg.alt = '';
    headImg.src = assetUrl(head.src);
    this.headLayer.appendChild(headImg);

    this.face = svg('svg', {
      class: 'buntu-face',
      viewBox: `0 0 ${this.panel.w} ${this.panel.h}`,
      width: this.panel.w,
      height: this.panel.h,
    });
    this.face.style.left = `${this.panel.x}px`;
    this.face.style.top = `${this.panel.y}px`;
    this.buildFace(atlas);
    this.headLayer.appendChild(this.face);

    // --- walking body ------------------------------------------------------
    this.walkLayer = document.createElement('div');
    this.walkLayer.className = 'buntu-layer';
    this.walkLayer.hidden = true;
    this.walkLayer.style.width = `${this.walkSize.w}px`;
    this.walkLayer.style.height = `${this.walkSize.h}px`;

    this.poseImg = document.createElement('img');
    this.poseImg.className = 'buntu-img';
    this.poseImg.alt = '';
    this.walkLayer.appendChild(this.poseImg);

    this.stage.append(this.headLayer, this.walkLayer);

    this.bubble = document.createElement('div');
    this.bubble.className = 'buntu-bubble';
    this.bubble.setAttribute('role', 'status');
    this.bubbleText = document.createElement('span');
    this.bubbleText.className = 'buntu-bubble-text';
    this.bubbleClose = document.createElement('button');
    this.bubbleClose.type = 'button';
    this.bubbleClose.className = 'buntu-bubble-close';
    this.bubbleClose.textContent = '\u00d7';
    this.bubbleClose.title = 'Mark as read';
    this.bubbleClose.setAttribute('aria-label', 'Mark message as read');
    this.bubbleClose.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.bubbleClose.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onDismiss?.();
    });
    this.bubble.append(this.bubbleText, this.bubbleClose);

    this.menu = this.buildMenu();
    this.inbox = this.buildInbox();
    this.inboxList = this.inbox.querySelector('.buntu-inbox-list') as HTMLElement;
    this.restore = this.buildRestore();

    this.root.append(this.stage, this.bubble, this.menu, this.inbox, this.restore);
    mount.appendChild(this.root);
    this.bindMenu();
  }

  private buildMenu(): HTMLElement {
    const menu = document.createElement('div');
    menu.className = 'buntu-menu';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', 'Buntu options');
    const items: Array<[MenuAction, string]> = [
      ['messages', 'Messages'],
      ['snooze', 'Snooze for an hour'],
      ['hide', 'Minimize for today'],
    ];
    for (const [action, label] of items) {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = label;
      b.setAttribute('role', 'menuitem');
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeMenu();
        this.onAction?.(action);
      });
      menu.appendChild(b);
    }
    return menu;
  }

  private buildInbox(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'buntu-inbox';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Buntu messages');
    panel.innerHTML = `
      <div class="buntu-inbox-head">
        <strong>Messages</strong>
        <button type="button" data-close="1">Close</button>
      </div>
      <div class="buntu-inbox-list"></div>
    `;
    panel.querySelector('[data-close]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closeInbox();
    });
    panel.addEventListener('pointerenter', () => {
      window.clearTimeout(this.menuTimer);
    });
    return panel;
  }

  private buildRestore(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'buntu-restore';
    btn.setAttribute('aria-label', 'Show Buntu');
    const img = document.createElement('img');
    img.alt = '';
    img.src = this.assetUrl(this.atlas.head.src);
    const label = document.createElement('span');
    label.textContent = 'Show Buntu';
    btn.append(img, label);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onRestore?.();
    });
    return btn;
  }

  private bindMenu(): void {
    // The menu opens on a single click (see Engine.finishPress), not on hover,
    // so passing the cursor over Buntu no longer pops it open.
    this.menu.addEventListener('pointerenter', () => {
      window.clearTimeout(this.menuTimer);
    });
    this.stage.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.setMenu(true);
      }
    });
    this.onDocKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeInbox();
        this.closeMenu();
      }
    };
    this.onDocClick = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (t && (this.inbox.contains(t) || this.menu.contains(t) || this.stage.contains(t))) {
        return;
      }
      this.closeInbox();
      this.closeMenu();
    };
    document.addEventListener('keydown', this.onDocKey);
    document.addEventListener('click', this.onDocClick);
  }

  private setMenu(open: boolean): void {
    if (this.inboxOpen) return;
    this.menu.dataset.open = open && !this.menuLocked ? '1' : '0';
  }

  /** Single click on Buntu shows or hides the options overlay. */
  toggleMenu(): void {
    if (this.inboxOpen) {
      this.closeInbox();
      return;
    }
    this.setMenu(this.menu.dataset.open !== '1');
  }

  closeMenu(): void {
    window.clearTimeout(this.menuTimer);
    this.setMenu(false);
  }

  /** Suppresses the menu, e.g. while Buntu is away on a break. */
  setMenuLocked(locked: boolean): void {
    this.menuLocked = locked;
    if (locked) this.closeMenu();
  }

  showInbox(
    items: Array<{ from: string; body: string; when: string }>,
    opts?: {
      emptyText?: string;
      moreLabel?: string | null;
      onMore?: () => void;
    }
  ): void {
    this.closeMenu();
    this.inboxOpen = true;
    // Paging replaces the list, so keep the reader's place.
    const prevScroll = this.inboxList.scrollTop;
    this.inboxList.innerHTML = '';
    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'buntu-inbox-empty';
      empty.textContent = opts?.emptyText || 'No messages yet.';
      this.inboxList.appendChild(empty);
    } else {
      for (const item of items) {
        const row = document.createElement('div');
        row.className = 'buntu-inbox-item';
        row.innerHTML = `
          <div class="buntu-inbox-meta">
            <span class="buntu-inbox-from"></span>
            <span class="buntu-inbox-when"></span>
          </div>
          <div class="buntu-inbox-body"></div>
        `;
        (row.querySelector('.buntu-inbox-from') as HTMLElement).textContent = item.from;
        (row.querySelector('.buntu-inbox-when') as HTMLElement).textContent = item.when;
        (row.querySelector('.buntu-inbox-body') as HTMLElement).textContent = item.body;
        this.inboxList.appendChild(row);
      }
    }
    if (opts?.moreLabel && opts.onMore) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'buntu-inbox-more';
      more.textContent = opts.moreLabel;
      more.addEventListener('click', (e) => {
        e.stopPropagation();
        opts.onMore?.();
      });
      this.inboxList.appendChild(more);
    }
    this.inbox.dataset.open = '1';
    this.inboxList.scrollTop = prevScroll;
    this.place(this.inbox, this.lastAnchor.x, this.lastAnchor.above);
  }

  closeInbox(): void {
    this.inboxOpen = false;
    this.inbox.dataset.open = '0';
  }

  isInboxOpen(): boolean {
    return this.inboxOpen;
  }

  /**
   * Rebuilds the eyes and mouth as live vectors over the blanked face panel.
   *
   * Positions come from the atlas, which measured the artist's original
   * features before the pipeline erased them, so the drawn face lands exactly
   * where the painted one was.
   */
  private buildFace(atlas: Atlas): void {
    const ink = atlas.head.palette?.ink ?? '#253546';
    const { w: pw, h: ph } = this.panel;
    const boxes = atlas.head.face?.eyes ?? [];

    for (const frac of boxes) {
      const box: Rect = {
        x: frac.x * pw,
        y: frac.y * ph,
        w: frac.w * pw,
        h: frac.h * ph,
      };
      this.eyeBoxes.push(box);

      const g = svg('g', {});
      const cx = box.x + box.w / 2;
      const cy = box.y + box.h / 2;
      g.appendChild(
        svg('ellipse', {
          cx,
          cy,
          rx: box.w / 2,
          ry: box.h / 2,
          fill: ink,
        })
      );
      // The original art puts a small specular dot high on each eye; without
      // it the eyes read as flat holes.
      g.appendChild(
        svg('circle', {
          cx: cx + box.w * 0.17,
          cy: cy - box.h * 0.22,
          r: Math.max(1.4, box.w * 0.15),
          fill: '#ffffff',
          opacity: 0.94,
        })
      );
      this.eyes.push(g);
      this.face.appendChild(g);
    }

    const m = atlas.head.face?.mouth;
    const mx = (m?.x ?? 0.35) * pw;
    const my = (m?.y ?? 0.69) * ph;
    const mw = (m?.w ?? 0.31) * pw;
    const mh = (m?.h ?? 0.15) * ph;
    this.mouth = svg('path', {
      d: `M ${mx} ${my} Q ${mx + mw / 2} ${my + mh * 1.5} ${mx + mw} ${my}`,
      fill: 'none',
      stroke: ink,
      'stroke-width': Math.max(2, mh * 0.34),
      'stroke-linecap': 'round',
    });
    this.face.appendChild(this.mouth);
  }

  /** Writes one frame of state to the DOM. */
  apply(s: FrameState): void {
    const walking = s.mode === 'walk';
    this.headLayer.hidden = walking;
    this.walkLayer.hidden = !walking;

    const layer = walking ? this.walkLayer : this.headLayer;
    const w = layer.offsetWidth || parseFloat(layer.style.width);
    const h = layer.offsetHeight || parseFloat(layer.style.height);

    const shown = !s.minimized && s.opacity > 0.05;
    this.stage.style.opacity = s.minimized ? '0' : String(s.opacity);
    // Minimized / faded Buntu must not intercept clicks under him.
    this.stage.style.pointerEvents = shown ? 'auto' : 'none';
    this.stage.dataset.dragging = s.dragging ? '1' : '0';
    this.stage.style.transform =
      `translate3d(${Math.round(s.x - w / 2)}px,` +
      `${Math.round(s.y - h + s.bob)}px,0)`;
    this.restore.dataset.show = s.minimized ? '1' : '0';
    if (s.minimized) {
      this.closeMenu();
      this.bubble.dataset.show = '0';
    }

    if (walking) {
      const entry = pose(this.atlas, s.poseName);
      if (entry && this.currentPose !== s.poseName) {
        this.poseImg.src = this.assetUrl(entry.src);
        this.currentPose = s.poseName;
      }
      // Squash is applied about the feet so the walk cycle stays grounded.
      // The sprite is never mirrored: these are three-quarter front views and
      // flipping one would reverse the BUNTU logo on its chest. Direction of
      // travel reads from the movement itself.
      this.walkLayer.style.transform =
        `scaleX(${(2 - s.squash).toFixed(3)}) scaleY(${s.squash.toFixed(3)})`;
    } else {
      // A small counter-rotation makes the head lead the eyes.
      this.headLayer.style.transform =
        `rotate(${(s.yaw * 4.5).toFixed(2)}deg) translateX(${(s.yaw * 3).toFixed(2)}px)`;
      this.applyFace(s);
    }

    if (s.bubble !== this.currentBubble) {
      this.currentBubble = s.bubble;
      if (s.bubble) this.bubbleText.textContent = s.bubble;
      this.bubble.dataset.show = s.bubble ? '1' : '0';
    }
    this.bubble.dataset.dismissible = s.bubble && s.bubbleDismissible ? '1' : '0';
    const above = Math.round(s.y - h + s.bob - 14);
    this.lastAnchor = { x: s.x, y: s.y, above };
    if (s.bubble) {
      this.place(this.bubble, s.x, above);
    }
    if (this.menu.dataset.open === '1') {
      // Sits above the speech bubble when both happen to be up.
      this.place(this.menu, s.x, above - (s.bubble ? 60 : 0));
    }
    if (this.inbox.dataset.open === '1') {
      this.place(this.inbox, s.x, above);
    }
  }

  /**
   * Positions a centred popover, nudged inward so it cannot run off screen.
   *
   * Both callers are anchored on Buntu, who normally sits in a corner, so
   * without the clamp half of the panel would be cut off by the viewport.
   */
  private place(el: HTMLElement, x: number, top: number): void {
    const w = el.offsetWidth || 160;
    const half = w / 2;
    const cx = Math.min(Math.max(x, half + 10), window.innerWidth - half - 10);
    el.style.left = `${Math.round(cx)}px`;
    el.style.top = `${top}px`;
    // Keep the speech tail aimed at Buntu even when the panel was nudged.
    const tail = Math.min(Math.max(x - (cx - half), 14), w - 14);
    el.style.setProperty('--buntu-tail', `${Math.round(tail)}px`);
  }

  private applyFace(s: FrameState): void {
    const open = 1 - s.blink;
    for (let i = 0; i < this.eyes.length; i++) {
      const box = this.eyeBoxes[i];
      // Gaze travel is capped well inside the panel so the eyes never crowd
      // the bezel at full deflection.
      const dx = s.gazeX * box.w * 0.3;
      const dy = s.gazeY * box.h * 0.2;
      const cy = box.y + box.h / 2 + dy;
      this.eyes[i].setAttribute(
        'transform',
        `translate(${dx.toFixed(2)} ${dy.toFixed(2)}) ` +
          `translate(0 ${cy.toFixed(2)}) scale(1 ${Math.max(0.06, open).toFixed(3)}) ` +
          `translate(0 ${(-cy).toFixed(2)})`
      );
    }
    // The mouth rides a little with vertical gaze, which sells the head tilt.
    this.mouth.setAttribute(
      'transform',
      `translate(${(s.gazeX * 1.6).toFixed(2)} ${(s.gazeY * 1.4).toFixed(2)})`
    );
  }

  destroy(): void {
    window.clearTimeout(this.menuTimer);
    if (this.onDocKey) document.removeEventListener('keydown', this.onDocKey);
    if (this.onDocClick) document.removeEventListener('click', this.onDocClick);
    this.root.remove();
  }
}
